import { useProjectStore } from '../stores/projects.js'
import { useVerificationRunsStore } from '../stores/verificationRuns.js'
import { connectionId } from './liveConnection.js'

/**
 * Dove un evento dell'hub diventa un cambiamento negli store. Sta fuori dagli store di proposito:
 * quelli non devono sapere che esiste un canale in tempo reale, e questo resta l'unico posto in cui
 * si decide cosa rileggere e quando.
 */

// Una raffica di scritture sullo stesso processo (un tool che riempie dieci campi) deve costare una
// rilettura sola.
const REFETCH_DEBOUNCE = 250
// Le scadenze si ricavano dalle righe: un solo giro dopo che la raffica si è calmata.
const DEADLINE_DEBOUNCE = 1000

// Interruttore contro le tempeste: oltre questa soglia conviene una lista intera che N letture.
const STORM_WINDOW = 2000
const STORM_LIMIT = 15

const pendingRefetch = new Map()
let deadlineTimer = null
let stormMarks = []

export function onLiveEvent(name, event) {
  if (name === 'lockChanged') return onLock(event)
  if (name === 'projectChanged') return onProject(event)
}

function onProject(event) {
  if (!event?.projectId) return

  const projects = useProjectStore()

  // L'eco della propria scrittura. Il Backend la toglie già all'origine quando conosce la
  // connessione; questo secondo controllo copre i percorsi che non la portano (scritture dell'IA e
  // dei tool avviate da noi), dove rileggere sovrascriverebbe il form ancora aperto.
  if (event.originConnectionId && event.originConnectionId === connectionId()) return

  if (event.kind === 'deleted' || event.kind === 'archived' || event.kind === 'left') {
    projects.removeProject(event.projectId)
    if (event.kind === 'archived') projects.archiveTick++
    scheduleDeadlines(projects)
    return
  }

  // Il progresso di una verifica interessa solo se quel processo ce l'abbiamo davanti o in lista.
  if (event.kind === 'verificationProgress' || event.kind === 'verificationReset') {
    if (!projects.hasProject(event.projectId) && projects.openProjectId !== event.projectId) return
  }

  // Processo aperto con modifiche non salvate: si avvisa e basta. Il conflitto 409 al salvataggio
  // resta l'ultimo baluardo, ma con questo lo si scopre in tre secondi invece che dopo dieci minuti
  // di digitazione.
  if (projects.openProjectId === event.projectId && projects.currentProjectDirty) {
    projects.noteExternalChange(event.actor)
  }

  if (isStorm()) {
    scheduleBulkReload(projects)
    return
  }

  scheduleRefetch(projects, event)

  if (['created', 'updated', 'statusChanged', 'deleted', 'archived'].includes(event.kind)) {
    scheduleDeadlines(projects)
  }
}

function scheduleRefetch(projects, event) {
  clearTimeout(pendingRefetch.get(event.projectId))
  pendingRefetch.set(event.projectId, setTimeout(async () => {
    pendingRefetch.delete(event.projectId)

    // Guardia sull'ordine: le riletture corrono fra loro, e un inviluppo più vecchio di ciò che
    // abbiamo già non ha niente da aggiungere. La versione serve SOLO a evitare la chiamata: la
    // riga si aggiorna con quello che risponde l'API, mai con quello che dice l'evento.
    const known = projects.versionOf(event.projectId)
    if (event.kind === 'updated' && event.version != null && known != null && event.version <= known) return

    await projects.syncOne(event.projectId)
  }, REFETCH_DEBOUNCE))
}

function scheduleDeadlines(projects) {
  clearTimeout(deadlineTimer)
  deadlineTimer = setTimeout(() => projects.loadDeadlines(), DEADLINE_DEBOUNCE)
}

/** Un'importazione o una rinumerazione di stati tocca decine di processi in pochi secondi. */
function isStorm() {
  const now = Date.now()
  stormMarks = stormMarks.filter(t => now - t < STORM_WINDOW)
  stormMarks.push(now)
  return stormMarks.length > STORM_LIMIT
}

let bulkTimer = null
function scheduleBulkReload(projects) {
  for (const timer of pendingRefetch.values()) clearTimeout(timer)
  pendingRefetch.clear()

  clearTimeout(bulkTimer)
  bulkTimer = setTimeout(() => {
    projects.loadProjects(1, projects.statusFilter, true)
    projects.loadDeadlines()
  }, 500)
}

function onLock(event) {
  useVerificationRunsStore().applyLockEvent(event)
}

/**
 * Il recupero dopo una connessione o una riconnessione. Obbligatorio: SignalR non ritrasmette gli
 * eventi persi mentre si era giù, quindi senza questo giro la lista resterebbe sbagliata per
 * sempre. È lo stesso lavoro che facevano i timer, ma solo dopo un buco vero.
 */
export async function resyncAll() {
  const projects = useProjectStore()
  const runs = useVerificationRunsStore()
  const openProject = projects.openProjectId

  await Promise.allSettled([
    projects.loadProjects(1, projects.statusFilter, true),
    projects.loadDeadlines(),
    runs.loadActiveLocks(),
    // Il processo aperto si rilegge solo se nessuno ci sta scrivendo dentro.
    openProject && !projects.currentProjectDirty ? projects.syncOne(openProject) : Promise.resolve()
  ])
}
